import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { Avatar, Btn, Empty, Loading, Page, cx } from "../components/wf";
import { api, num, todayISO, weekStart } from "../lib/api";

export default function TeamHours() {
  const [offset, setOffset] = useState(0);
  const start = weekStart(offset);
  const q = useQuery({ queryKey: ["team-hours", start], queryFn: () => api(`/team-hours?start=${start}`) });
  const d = q.data;
  const today = todayISO();
  const day = (iso) => new Date(iso + "T00:00").toLocaleDateString("en-US", { weekday: "short", day: "numeric" });
  return (
    <Page title="Team Hours" subtitle={d ? `${d.start} – ${d.end} · ${num(d.total_hours)} hrs` : start} wide testid="team-hours-page"
      right={<div className="flex items-center gap-1">
        <Btn variant="outline" size="sm" icon={ChevronLeft} onClick={() => setOffset((o) => o - 1)} data-testid="team-hours-prev">Prev</Btn>
        {offset !== 0 && <Btn variant="ghost" size="sm" onClick={() => setOffset(0)} data-testid="team-hours-this-week">This week</Btn>}
        <Btn variant="outline" size="sm" disabled={offset >= 0} onClick={() => setOffset((o) => o + 1)} data-testid="team-hours-next">Next <ChevronRight size={16} /></Btn>
      </div>}>
      {q.isLoading ? <Loading /> : !d ? <Empty title="Couldn't load team hours" subtitle={q.error?.message} /> : (
        <div className="overflow-x-auto rounded-2xl border border-wf-line" data-testid="team-hours-table">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="bg-wf-s2 text-left text-xs font-bold uppercase tracking-wide text-wf-mute">
                <th className="px-4 py-3">Team member</th>
                {d.dates.map((dt) => <th key={dt} className={cx("px-2 py-3 text-center", dt === today && "text-wf-brand")}>{day(dt)}</th>)}
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-wf-line bg-wf-soft font-bold text-wf-softink">
                <td className="px-4 py-3">Team</td>
                {d.day_totals.map((h, i) => <td key={i} className="px-2 py-3 text-center">{h ? num(h) : "–"}</td>)}
                <td className="px-4 py-3 text-right" data-testid="team-hours-total">{num(d.total_hours)}</td>
              </tr>
              {d.people.map((p) => (
                <tr key={p.id} data-testid={`team-hours-row-${p.id}`} className={cx("border-t border-wf-line", p.status !== "active" && "opacity-60")}>
                  <td className="px-4 py-3">
                    <Link to={`/employees/${p.id}`} className="flex items-center gap-2 font-semibold hover:underline"><Avatar name={p.name} src={p.photo_path} size={30} />{p.name}</Link>
                  </td>
                  {p.days.map((h, i) => <td key={i} className={cx("px-2 py-3 text-center", h ? "font-bold" : "text-wf-line2")}>{h ? num(h) : "–"}</td>)}
                  <td className="px-4 py-3 text-right font-extrabold">{num(p.total_hours)}<span className="block text-[11px] font-medium text-wf-mute">{p.jobs} job{p.jobs === 1 ? "" : "s"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  );
}
